import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin } from 'api/types';
import BusinessUnitSelect from 'components/common/org/BusinessUnitSelect';
import SectionedModalShell, { ShellBlockers, ShellChip, type ShellBlocker, type ShellSection } from 'components/modals/common/SectionedModalShell';
import DineOpsOutletTaxProfileModal from './DineOpsOutletTaxProfileModal';
import DineOpsOutletStationsModal from './DineOpsOutletStationsModal';
import DineOpsOutletMealPeriodsModal from './DineOpsOutletMealPeriodsModal';
import {
  buffetSummary, roomChargeBlocked, stationsMissing, taxMissing, taxSummary, type OutletSection,
} from 'components/modules/dineops-module/masters/outletWorkspace';

/**
 * Add an outlet, or open one as the outlet window (2026-10-28): every part of its setup in one place —
 * General, Billing & limits, Tax, Kitchen stations, Meal periods, Room charging — each with a status
 * mark from the Setup Checklist's rules. General and Billing save together with Save; Tax, Stations and
 * Meal periods keep their own saves (dated history, lists) and say so. Adding an outlet stays a form.
 *
 * Add / edit an outlet (SETUP-006, multi-outlet). A new outlet joins the same
 * property and takes its quick notes and charge types from an existing outlet
 * (the oldest). The menu is shared (DO-312): the new outlet sells nothing until
 * its kitchen stations are ticked and dishes are listed on Outlet Items. The
 * code prints in order and invoice numbers and cannot change after creation.
 *
 * Business unit: the organisation unit the outlet's figures belong to, usually
 * the F&B department, from the outlet's entity. A new outlet starts with the
 * copied outlet's unit, shown so it can be changed; nothing is copied silently.
 */

/** Unit types an outlet links to: the F&B department, or a unit under it. */
const OUTLET_UNIT_TYPES = ['DEPARTMENT', 'COST_CENTER', 'DIVISION'];

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DineOutletAdmin | null;
  /** Every outlet, so a new one knows the outlet it copies (the oldest): its property, entity and unit. */
  outlets?: DineOutletAdmin[];
  onClose: () => void;
  onSaved: () => void;
  /** Edit: the section to open on (the Setup Checklist's Go, a grid status cell). */
  initialSection?: OutletSection;
  /** Edit: may change stations and meal periods (the Outlets screen's edit action). */
  canEdit?: boolean;
}

interface FormData {
  outletCode: string;
  outletName: string;
  address: string;
  gstin: string;
  fssaiNumber: string;
  businessDayCutoff: string;
  serviceChargePct: string;
  discountLimitPct: string;
  openItemLimit: string;
  maxBillsPerSplit: string;
  fallbackAlertMinutes: string;
  requisitionTransferPct: string;
  isActive: boolean;
  serviceMode: 'TABLES' | 'ROOM_SERVICE';
  businessUnitId: string;
}

const INITIAL: FormData = {
  outletCode: '', outletName: '', address: '', gstin: '', fssaiNumber: '',
  businessDayCutoff: '', serviceChargePct: '0', discountLimitPct: '10',
  openItemLimit: '500', maxBillsPerSplit: '6', fallbackAlertMinutes: '3', requisitionTransferPct: '40', isActive: true, serviceMode: 'TABLES',
  businessUnitId: '',
};

/** The outlet a new one copies: the oldest, as the server picks it. */
const oldestOutlet = (outlets: DineOutletAdmin[] = []) =>
  [...outlets].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.outletCode.localeCompare(b.outletCode))[0];

const fromRecord = (r: DineOutletAdmin): FormData => ({
  outletCode: r.outletCode, outletName: r.outletName, address: r.address ?? '',
  gstin: r.gstin ?? '', fssaiNumber: r.fssaiNumber ?? '', businessDayCutoff: (r.businessDayCutoff ?? '').slice(0, 5),
  serviceChargePct: String(r.serviceChargePct), discountLimitPct: String(r.discountLimitPct),
  openItemLimit: String(r.openItemLimit), maxBillsPerSplit: String(r.maxBillsPerSplit),
  fallbackAlertMinutes: String(r.fallbackAlertMinutes), requisitionTransferPct: String(r.requisitionTransferPct ?? 40), isActive: r.isActive,
  serviceMode: r.isRoomService ? 'ROOM_SERVICE' : 'TABLES',
  businessUnitId: r.businessUnitId ?? '',
});

const DineOpsOutletFormModal: React.FC<Props> = ({ isOpen, mode, record, outlets, onClose, onSaved, initialSection = 'general', canEdit = true }) => {
  const [formData, setFormData] = useState<FormData>(INITIAL);
  const initialSnapshotRef = useRef('');
  const recordRef = useRef(record);
  recordRef.current = record;
  const template = useMemo(() => oldestOutlet(outlets), [outlets]);
  const templateRef = useRef(template);
  templateRef.current = template;
  // Whose entity and property the unit picker uses: the outlet itself, or the outlet a new one copies.
  const scope = mode === 'edit' ? record : template;

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [section, setSection] = useState<OutletSection>('general');
  // Unsaved edits inside the Tax / Stations / Meal periods sections (they save on their own).
  const [paneDirty, setPaneDirty] = useState<Record<string, boolean>>({});
  const markTax = useCallback((d: boolean) => setPaneDirty((p) => (p.tax === d ? p : { ...p, tax: d })), []);
  const markStations = useCallback((d: boolean) => setPaneDirty((p) => (p.stations === d ? p : { ...p, stations: d })), []);
  const markMeals = useCallback((d: boolean) => setPaneDirty((p) => (p.meals === d ? p : { ...p, meals: d })), []);
  // The sections load once per outlet: a list refresh after one section saves must not reload (and
  // drop unsaved edits in) another, so they get an outlet that changes only with the outlet id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paneOutlet = useMemo(() => record ?? null, [record?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const data = mode === 'edit' && recordRef.current
      ? fromRecord(recordRef.current)
      : { ...INITIAL, businessUnitId: templateRef.current?.businessUnitId ?? '' };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setServerError(null);
    setSection(initialSection);
    setPaneDirty({});
  }, [isOpen]);

  const isDirty = useMemo(
    () => !!initialSnapshotRef.current && JSON.stringify(formData) !== initialSnapshotRef.current,
    [formData],
  );

  const set = (key: keyof FormData, value: string | boolean) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const anyPaneDirty = Object.values(paneDirty).some(Boolean);
  const handleClose = useCallback(() => {
    if (isDirty || anyPaneDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, anyPaneDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (mode === 'create' && !/^[A-Za-z0-9]{2,10}$/.test(formData.outletCode.trim()))
      errors.outletCode = 'The code is 2 to 10 letters or digits, e.g. JUC.';
    if (!formData.outletName.trim()) errors.outletName = 'The outlet needs a name.';
    const pct = (v: string) => Number(v) >= 0 && Number(v) <= 100;
    if (!pct(formData.serviceChargePct)) errors.serviceChargePct = '0 to 100.';
    if (!pct(formData.discountLimitPct)) errors.discountLimitPct = '0 to 100.';
    if (mode === 'edit' && (formData.requisitionTransferPct.trim() === '' || !pct(formData.requisitionTransferPct)))
      errors.requisitionTransferPct = '0 to 100.';
    if (!(Number(formData.openItemLimit) >= 0)) errors.openItemLimit = 'Cannot be negative.';
    const bills = Number(formData.maxBillsPerSplit);
    if (!(bills >= 2 && bills <= 20)) errors.maxBillsPerSplit = '2 to 20.';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setSaveConfirmOpen(true);
  }, [formData, mode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    setServerError(null);
    const common = {
      outletName: formData.outletName.trim(),
      address: formData.address.trim() || undefined,
      gstin: formData.gstin.trim() || undefined,
      fssaiNumber: formData.fssaiNumber.trim() || undefined,
      businessDayCutoff: formData.businessDayCutoff || undefined,
      serviceChargePct: Number(formData.serviceChargePct) || 0,
      discountLimitPct: Number(formData.discountLimitPct) || 0,
      openItemLimit: Number(formData.openItemLimit) || 0,
      maxBillsPerSplit: Number(formData.maxBillsPerSplit) || 6,
    };
    try {
      if (mode === 'edit' && record) {
        await dineOpsService.updateOutlet(record.id, {
          ...common, fallbackAlertMinutes: Number(formData.fallbackAlertMinutes) || 3,
          requisitionTransferPct: Number(formData.requisitionTransferPct),
          isActive: formData.isActive, businessUnitId: formData.businessUnitId || null, rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`"${common.outletName}" updated successfully.`);
      } else {
        await dineOpsService.createOutlet({
          ...common, outletCode: formData.outletCode.trim().toUpperCase(), serviceMode: formData.serviceMode,
          businessUnitId: formData.businessUnitId || undefined,
        });
        notificationService.toast.success(formData.serviceMode === 'ROOM_SERVICE'
          ? `"${common.outletName}" created as the Room Service outlet. Tick its kitchen stations, add dishes on Outlet Items, and give staff access.`
          : `"${common.outletName}" created. Tick its kitchen stations, then add dishes on Outlet Items.`);
      }
      setIsSaving(false);
      onSaved();
      if (mode === 'edit') initialSnapshotRef.current = JSON.stringify(formData);
      else onClose();
    } catch (err: any) {
      setIsSaving(false);
      setServerError(err?.response?.data?.message ?? 'That could not be saved.');
    }
  }, [formData, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  const err = (k: string) => fieldErrors[k];
  const input = (key: keyof FormData, label: React.ReactNode, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="form-group" data-field={key}>
      <label className="form-label" htmlFor={`outlet-${key}`}>{label}</label>
      <input id={`outlet-${key}`} className={`form-input${err(key) ? ' form-field-error' : ''}`} value={String(formData[key])}
        onChange={(e) => set(key, e.target.value)} {...props} />
      {err(key) && <div className="form-error-message">{err(key)}</div>}
    </div>
  );

  const general = (
    <>
            <div className="form-grid-2">
              <div className="form-group" data-field="outletCode">
                <label className="form-label" htmlFor="outlet-outletCode">Code <span className="text-danger">*</span></label>
                <input id="outlet-outletCode" className={`form-input${err('outletCode') ? ' form-field-error' : ''}`}
                  value={formData.outletCode} disabled={mode === 'edit'} placeholder="e.g., JUC"
                  onChange={(e) => set('outletCode', e.target.value.toUpperCase())} />
                {err('outletCode') && <div className="form-error-message">{err('outletCode')}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Prints in order and invoice numbers. Cannot change later.</div>
              </div>
              {input('outletName', <>Outlet name <span className="text-danger">*</span></>, { placeholder: 'e.g., Juice Counter' })}
            </div>
            <div className="form-group" data-field="serviceMode">
              <label className="form-label" htmlFor="outlet-serviceMode">Service</label>
              <select id="outlet-serviceMode" className="form-input" value={formData.serviceMode} disabled={mode === 'edit'}
                onChange={(e) => set('serviceMode', e.target.value as FormData['serviceMode'])}>
                <option value="TABLES">Tables — a restaurant with a floor</option>
                <option value="ROOM_SERVICE">Room service — no tables, room orders only</option>
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {formData.serviceMode === 'ROOM_SERVICE'
                  ? 'One per property. Room service orders are then taken only here; restaurant waiters no longer see a Room service button. Bills can go to the guest\'s room.'
                  : 'Waiters seat guests at tables.'}{mode === 'edit' ? ' Cannot change after creation.' : ''}
              </div>
            </div>
          {input('address', 'Address')}
            <div className="form-group" data-field="businessUnitId">
              <BusinessUnitSelect
                value={formData.businessUnitId}
                onChange={(v) => set('businessUnitId', v)}
                entityId={scope?.entityId}
                propertyId={scope?.propertyId}
                types={OUTLET_UNIT_TYPES}
                seed={mode === 'edit' ? { id: record?.businessUnitId, name: record?.businessUnitName } : { id: template?.businessUnitId, name: template?.businessUnitName }}
                placeholder="Not linked yet"
                helperText="Where the outlet's figures belong in the organisation, usually the F&B department. Needed when accounts posting is switched on."
              />
            </div>
    </>
  );
  const billingFields = (
    <>
            <div className="form-grid-3">
              {input('gstin', 'GSTIN', { placeholder: 'Same as property' })}
              {input('fssaiNumber', 'FSSAI number')}
              {input('businessDayCutoff', 'Business day ends at', { type: 'time' })}
            </div>
            {/* Settings cleanup C6: blank = the property's value, so the two cannot drift apart. */}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '-4px 0 10px' }}>
              Leave GSTIN or the business-day time blank to use the property&apos;s. Fill them only where this outlet differs,
              such as a bar that closes after midnight.
            </div>
            <div className="form-grid-4">
              {input('serviceChargePct', 'Service charge %', { type: 'number', min: 0, max: 100 })}
              {input('discountLimitPct', 'Discount limit %', { type: 'number', min: 0, max: 100 })}
              {input('openItemLimit', 'Open item limit', { type: 'number', min: 0 })}
              {input('maxBillsPerSplit', 'Bills per split', { type: 'number', min: 2, max: 20 })}
            </div>
    </>
  );

  if (mode === 'edit' && record) {
    const tax = taxSummary(record);
    const buffet = buffetSummary(record);
    const blockers: ShellBlocker[] = [
      ...(taxMissing(record) ? [{ label: 'No tax profile', sectionId: 'tax' }] : []),
      ...(stationsMissing(record) ? [{ label: 'No kitchen station', sectionId: 'stations' }] : []),
      ...(roomChargeBlocked(record) ? [{ label: 'Room charging not allowed', sectionId: 'room' }] : []),
    ];
    const ownSave = (what: string) => (
      <div className="setup-sheet-note" style={{ marginBottom: 8 }}>{what} save on their own, here; the window's Save is for General and Billing.</div>
    );
    const sections: ShellSection[] = [
      { id: 'general', title: 'General', badge: record.businessUnitId ? 'ok' : 'warn', content: (
        <>
          {serverError && section === 'general' && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          {general}
              {mode === 'edit' && (
                <div className="form-grid-3">
                  {input('fallbackAlertMinutes', 'Fallback alert (minutes)', { type: 'number', min: 1, max: 120 })}
                  {input('requisitionTransferPct', 'Requisition transfer %', {
                    type: 'number', min: 0, max: 100, step: '0.01',
                    title: 'A dish without its own transfer price goes on a department requisition at this percentage of its menu price. No tax.',
                  })}
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={formData.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                      <span>Active</span>
                    </label>
                  </div>
                </div>
              )}
        </>
      ) },
      { id: 'billing', title: 'Billing & limits', badge: 'ok', content: (
        <>
          {serverError && section === 'billing' && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          {billingFields}
          <div className="setup-sheet-note">
            Blank GSTIN: {record.propertyGstin || 'the property has none'}. Blank day end: {(record.propertyBusinessDayCutoff ?? '').slice(0, 5) || 'the property\'s'}.
          </div>
        </>
      ) },
      { id: 'tax', title: 'Tax', badge: taxMissing(record) ? 'warn' : (tax ? 'ok' : undefined), content: (
        <>
          {!record.taxCalculationEnabled && (
            <div className="setup-sheet-note" style={{ marginBottom: 8 }}>No tax is calculated for this outlet: its entity is not registered as Regular (Setup › Tax &amp; Billing › Tax Registrations), or, with no registration set, Tax Calculation is off in Settings › Modules. A profile applies once tax is on.</div>
          )}
          {ownSave('Tax profile rows')}
          <DineOpsOutletTaxProfileModal isOpen outlet={paneOutlet} onClose={() => undefined} embedded onDirtyChange={markTax} onSaved={onSaved} />
        </>
      ) },
      { id: 'stations', title: 'Kitchen stations', badge: stationsMissing(record) ? 'warn' : 'ok', content: (
        <>
          {ownSave('Stations')}
          <DineOpsOutletStationsModal isOpen outlet={paneOutlet} canEdit={canEdit} onClose={() => undefined} onSaved={onSaved} embedded onDirtyChange={markStations} />
        </>
      ) },
      { id: 'meals', title: 'Meal periods', badge: buffet ? 'ok' : undefined, content: (
        <>
          {ownSave('Meal periods')}
          <DineOpsOutletMealPeriodsModal isOpen outlet={paneOutlet} canEdit={canEdit} onClose={() => undefined} embedded onDirtyChange={markMeals} onSaved={onSaved} />
        </>
      ) },
      { id: 'room', title: 'Room charging', badge: roomChargeBlocked(record) ? 'warn' : undefined, content: (
        <div className="setup-sheet-note" style={{ fontSize: 13 }}>
          {record.pmsMode !== 'ATTACHED' || !record.propertyId
            ? 'This outlet is not attached to the PMS, so bills are settled at the outlet and not charged to guest rooms.'
            : record.roomChargeAllowed
              ? `Bills can be settled to a guest's room at ${record.propertyName}.`
              : `Room charging is not allowed for ${record.propertyName}: bills cannot be settled to a guest's room. It is set where the CRS outlet is linked to the property.`}
        </div>
      ) },
    ];

    return (
      <>
        <SectionedModalShell
          title={`Outlet — ${record.outletName} (${record.outletCode})`}
          summary={(
            <>
              <ShellChip tone="info">{record.propertyName ?? 'Standalone'}</ShellChip>
              <ShellChip tone={record.isActive ? 'ok' : 'warn'}>{record.isActive ? 'Active' : 'Inactive'}</ShellChip>
              <ShellChip tone={taxMissing(record) ? 'warn' : 'info'}>{tax ?? (record.taxCalculationEnabled ? 'No tax profile' : 'Tax off')}</ShellChip>
              <ShellChip tone="info">{buffet ? `Buffet: ${buffet}` : 'No buffet'}</ShellChip>
              <ShellChip tone={stationsMissing(record) ? 'warn' : 'info'}>{record.stationCount} kitchen station{record.stationCount === 1 ? '' : 's'}</ShellChip>
            </>
          )}
          sections={sections}
          activeSection={section}
          onSectionChange={(id) => setSection(id as OutletSection)}
          onClose={handleClose}
          busy={isSaving}
          loading={{ active: isSaving, label: 'Saving...' }}
          maxWidth="1100px"
          footer={(
            <>
              <ShellBlockers blockers={blockers} onJump={(id) => setSection(id as OutletSection)} />
              <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Close</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !isDirty}
                title={!isDirty ? 'No changes to General or Billing' : undefined}>Save</button>
            </>
          )}
        />
        <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
          message={`Save the changes to "${formData.outletName.trim()}"?`}
          confirmLabel="Confirm" cancelLabel="Cancel" variant="default"
          onConfirm={() => void handleConfirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
        <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
          confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
          onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
      </>
    );
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 760 }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--color-primary, #2563eb)',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add Outlet</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          {general}
          {billingFields}
          {mode === 'create' && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              The new outlet joins the same property and takes its quick notes and charge types from an existing outlet.
              The menu is shared: tick its kitchen stations, then add the dishes it sells on Outlet Items.
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
        message={mode === 'create' ? `Create the outlet "${formData.outletName.trim()}"?` : `Save the changes to "${formData.outletName.trim()}"?`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default"
        onConfirm={() => void handleConfirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default DineOpsOutletFormModal;
